/* eslint-disable no-undef */
/* global navigator */
import React, { useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import CameraIcon from 'apps/web_forms/assets/cameraicon.svg';
import SwitchCameraIcon from 'apps/web_forms/assets/switch-camera.svg';
import RefreshIcon from 'apps/web_forms/assets/refresh-icon.svg';
import {
  addImageInputValue,
  setCurrentSelectedMultimediaInput,
} from 'apps/web_forms/slices/multimediaSlice';
import { COLORS, FONT_WEIGHT } from 'apps/web_forms/utils/constants';
import BarcodeCropper from 'apps/web_forms/components/commons/BarcodeCropper';
import Loader from 'react-loader-spinner';

import soundfile from '../assets/beep.mp3';

const Canvas = styled.canvas`
  display: none;
`;

const StyledCropperContainer = styled.div`
  position: relative;

  button {
    position: absolute;
    background: white;
    width: 2em;
    height: 2em;
    top: -0.8em;
    right: -0.8em;
    color: white;
    border: none;
    border-radius: 5em;
    z-index: 10;

    svg {
      width: 100%;
      height: 100%;
      fill: ${COLORS.grey};
    }
  }
`;

const CameraButton = styled.button`
  background-color: white;
  border: none;
  font-size: 0.9em;
  font-weight: ${FONT_WEIGHT.bold};
  display: flex;
  align-items: center;
  margin-top: 1em;
  align-self: flex-start;
  svg {
    margin-right: 0.5em;
  }
`;

const ErrorMessage = styled.p`
  color: ${COLORS.red};
`;

const Video = styled.video`
  -webkit-transform: ${(props) =>
    props.mirrorVideo ? 'scaleX(-1)' : 'scaleX(1)'};
  transform: ${(props) => (props.mirrorVideo ? 'scaleX(-1)' : 'scaleX(1)')};
`;

const COMPRESS_PICTURE_MAX_SIZE = {
  width: 480,
  height: 360,
};

const TakePhotoFromCamera = ({ element, scanBarcode }) => {
  const cameraContainer = useRef();
  const canvasEle = useRef();
  const [cameraOn, setCameraOn] = useState(false);
  const [imageSize, setImageSize] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useDispatch();

  const [isMultiCamera, setIsMultiCamera] = useState(false);
  const [switchCamera, setSwitchCamera] = useState(false);
  const [loadingCamera, setLoadingCamera] = useState(false);

  const beepAudio = useRef();

  const [croppingImageSrc, setCroppingImageSrc] = useState();

  const compressResult = useMemo(() => {
    return !element.extra_large && element.type === 'image';
  }, [element]);

  const checkAndSetMultiCamera = () => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const multiCamera =
        devices.filter((d) => d.kind === 'videoinput').length > 1;

      setIsMultiCamera(multiCamera);
    });
  };

  useEffect(checkAndSetMultiCamera, []);

  useEffect(() => {
    if (!croppingImageSrc) {
      setSwitchCamera(false);
      turnOnCamera();
    }

    return () => {
      stopVideo();
    };
  }, [element, croppingImageSrc]);

  const turnOnCamera = () => {
    let stream = null;

    // eslint-disable-next-line no-undef
    beepAudio.current = new Audio(soundfile);

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });

        const video = cameraContainer.current;
        video.srcObject = stream;
        video.onloadedmetadata = () => {
          video.play();
          setCameraOn(true);
        };
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          setErrorMessage(error.message);
        }
        // eslint-disable-next-line no-console
        console.log(error);
      }
    };

    start().then(checkAndSetMultiCamera);
  };

  const playVideo = () => {
    let streaming = false;
    const width = '100%';
    let height = 0;
    const canvas = canvasEle.current;
    const video = cameraContainer.current;

    if (!streaming) {
      height = video.clientHeight;
      video.setAttribute('width', width);
      canvas.setAttribute('width', width);
      streaming = true;
      setImageSize({ width, height });
    }
  };

  const captureFrame = () => {
    const context = canvasEle.current.getContext('2d');

    const width = compressResult
      ? COMPRESS_PICTURE_MAX_SIZE.width
      : cameraContainer.current.videoWidth;

    const height = compressResult
      ? COMPRESS_PICTURE_MAX_SIZE.height
      : cameraContainer.current.videoHeight;

    imageSize.width = width;
    imageSize.height = height;

    canvasEle.current.width = width;
    canvasEle.current.height = height;
    context.drawImage(cameraContainer.current, 0, 0, width, height);

    return canvasEle.current;
  };

  const takePhoto = () => {
    const currentCanvas = captureFrame();

    if (compressResult) {
      currentCanvas.toBlob(
        (blob) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onload = () => {
            storePicture(reader.result);
          };
        },
        'image/png',
        0.6
      );
    } else {
      storePicture(currentCanvas.toDataURL('image/png'));
    }
  };

  const storePicture = (frame) => {
    stopVideo();
    dispatch(setCurrentSelectedMultimediaInput({}));
    dispatch(
      addImageInputValue({
        key: element.identifier,
        value: frame,
      })
    );
  };

  const takePhotoAndCrop = () => {
    const currentCanvas = captureFrame();
    const frame = currentCanvas.toDataURL('image/png');
    setCroppingImageSrc(frame);
    stopVideo();
  };

  const switchCameraFunc = async () => {
    if (!loadingCamera) {
      setSwitchCamera(!switchCamera);
      setLoadingCamera(true);

      stopVideo();

      const newFacingMode = switchCamera ? 'environment' : 'user';

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacingMode },
        audio: false,
      });

      const video = cameraContainer.current;
      video.srcObject = stream;

      video.onloadedmetadata = () => {
        video.play();
        setLoadingCamera(false);
      };
    }
  };

  const stopVideo = () => {
    if (!cameraContainer.current) return;
    if (!cameraContainer.current.srcObject) return;
    const video = cameraContainer.current;

    video.pause();
    video.src = '';
    video.srcObject.getTracks().forEach((track) => {
      track.stop();
    });

    video.srcObject = null;
  };

  return (
    <>
      {loadingCamera && <Loader type="Oval" color={COLORS.primary} />}
      {!croppingImageSrc && (
        <>
          <Video
            mirrorVideo={
              isMultiCamera ? scanBarcode && switchCamera : scanBarcode
            }
            id="camera-container"
            ref={cameraContainer}
            onCanPlay={playVideo}
            style={{ display: loadingCamera ? 'none' : 'initial' }}
          >
            <track kind="captions" />
          </Video>
        </>
      )}
      {croppingImageSrc && (
        <StyledCropperContainer>
          <BarcodeCropper
            element={element}
            uploadedImageSrc={croppingImageSrc}
          />
          <button type="button" onClick={() => setCroppingImageSrc('')}>
            <RefreshIcon />
          </button>
        </StyledCropperContainer>
      )}
      {isMultiCamera && !loadingCamera && cameraOn && !croppingImageSrc && (
        <CameraButton
          onClick={() => switchCameraFunc()}
          type="button"
          aria-label="Switch camera"
        >
          <SwitchCameraIcon />
          Switch camera
        </CameraButton>
      )}
      {!loadingCamera && cameraOn && scanBarcode && !croppingImageSrc && (
        <CameraButton
          onClick={takePhotoAndCrop}
          type="button"
          aria-label="Take Photo"
        >
          <CameraIcon />
          Capture barcode
        </CameraButton>
      )}
      {cameraOn && !scanBarcode && (
        <CameraButton onClick={takePhoto} type="button" aria-label="Take Photo">
          <CameraIcon />
          Capture Image
        </CameraButton>
      )}

      <Canvas id="canvas" ref={canvasEle} />
      {errorMessage !== undefined && (
        <ErrorMessage id={`${element.identifier}_errmsg`}>
          {errorMessage}
        </ErrorMessage>
      )}
    </>
  );
};

TakePhotoFromCamera.propTypes = {
  element: PropTypes.shape({
    identifier: PropTypes.string,
    barcode_type: PropTypes.string,
    type: PropTypes.string,
    extra_large: PropTypes.bool,
  }).isRequired,
  scanBarcode: PropTypes.bool,
};
TakePhotoFromCamera.defaultProps = {
  scanBarcode: false,
};

export default TakePhotoFromCamera;
